import { Box, Divider, Group, ScrollArea, Text, UnstyledButton } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { useEffect, useRef } from 'react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { ChatSkill, SkillMenuRows, skillMenuOptions } from '../chats/skillCommands';
import { skillLabel } from 'src/common/utils/skillNames';

type Props = {
  rows: SkillMenuRows;
  // Null when no row is highlighted, so Enter in the composer sends the message instead.
  activeIndex: number | null;
  onHover: (index: number) => void;
  onSelect: (skill: ChatSkill) => void;
};

const MENU_ID = 'siesta-skill-command-menu';

export function skillOptionId(skill: ChatSkill) {
  return `${MENU_ID}-option-${skill.id}`;
}

export { MENU_ID as SKILL_COMMAND_MENU_ID };

/**
 * The list that opens when a message starts a slash command: the firm's own skills in sentence
 * case, the built-in management entry points the user's role allows, and a pinned "create a new
 * skill" row, filtered by what has been typed so far. Keyboard handling lives in the composer so
 * the textarea keeps focus; this component only renders the options and reports hovers and clicks.
 */
export function SkillCommandMenu({ rows, activeIndex, onHover, onSelect }: Props) {
  const activeRowRef = useRef<HTMLButtonElement>(null);
  const options = skillMenuOptions(rows);
  const firmSkills = rows.matches.filter(skill => skill.kind === 'firm');
  const builtInSkills = rows.matches.filter(skill => skill.kind === 'builtin');

  useEffect(() => {
    activeRowRef.current?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const renderOption = (skill: ChatSkill) => {
    const index = options.indexOf(skill);
    const isActive = index === activeIndex;
    const isCreate = skill === rows.createSkill;
    return (
      <UnstyledButton
        key={skill.id}
        ref={isActive ? activeRowRef : undefined}
        id={skillOptionId(skill)}
        role="option"
        aria-selected={isActive}
        w="100%"
        px="sm"
        py="xs"
        onMouseEnter={() => onHover(index)}
        onMouseDown={event => event.preventDefault()}
        onClick={() => onSelect(skill)}
        style={{
          backgroundColor: isActive ? 'var(--mantine-color-gray-8)' : 'transparent',
        }}
      >
        <Group gap="xxxs" wrap="nowrap">
          {isCreate && <IconPlus size={14} color="white" />}
          <Text size="sm" c="white" fw={FONT_WEIGHTS.medium} lineClamp={1}>
            {skillLabel(skill)}
          </Text>
        </Group>
        <Text size="xs" c="gray.4" lineClamp={2}>
          {skill.description}
        </Text>
      </UnstyledButton>
    );
  };

  return (
    <Box
      id={MENU_ID}
      role="listbox"
      aria-label="Skills"
      mb="sm"
      style={{
        borderRadius: 12,
        border: '1px solid var(--mantine-color-gray-7)',
        // Opaque: the composer sits over the chat surface and its animated flames, which a
        // translucent tint lets through and makes the options unreadable.
        backgroundColor: '#1a1a1a',
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.4)',
        overflow: 'hidden',
      }}
    >
      <Text size="xs" c="gray.4" px="sm" pt="xs" pb="xxxs">
        {options.length === 0 ? 'No matching skills' : 'Use a skill for this message'}
      </Text>
      {options.length > 0 && (
        <ScrollArea.Autosize mah={260} type="auto">
          {firmSkills.map(renderOption)}
          {firmSkills.length > 0 && (builtInSkills.length > 0 || rows.createSkill) && (
            <Divider color="gray.7" />
          )}
          {builtInSkills.map(renderOption)}
          {rows.createSkill && renderOption(rows.createSkill)}
        </ScrollArea.Autosize>
      )}
    </Box>
  );
}
